import { useGetProFormasByDeal } from 'queries/ProForma/useGetProFormasByDeal';
import { useGetProjectBudgetScenarios } from 'queries/BudgetScenario/useGetProjectBudgetScenarios';
import useGetProFormaRoom from 'queries/ProForma/useGetProFormaRoom';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import { useAuthContext } from 'contexts/AuthContext';
import { getLocalStorage, getEffectiveKey } from 'helpers/localStorage/helpers';
import { IProFormaPageLocalStorage } from 'views/ProFormaTable/types';
import { PageSpecificKeys, GlobalKeys } from 'helpers/localStorage';
import { SELECTED_SCENARIO_KEY } from 'views/ProFormaTable/constants';
import { getCookie } from 'helpers/localStorage/cookies';

interface IUseProjectActualsForDealParams {
    dealId: number;
    enabled?: boolean;
}

export const useProjectActualsForDeal = ({ dealId, enabled = true }: IUseProjectActualsForDealParams) => {
    const { selectedOrganizationId, user } = useAuthContext();

    const { data: proformas } = useGetProFormasByDeal({
        dealId,
        proFormaStatusIds: [PRO_FORMA_STATUS.PROJECT_CREATED],
    }, {
        enabled,
        staleTime: Infinity,
        gcTime: 1000 * 60 * 60,
        refetchOnWindowFocus: false,
    });

    const activeProformaWithProject = proformas?.find(pf => pf.isActive && pf.subprojectId);

    const { data: budgetScenariosData, isLoading: isLoadingBudgetScenarios } = useGetProjectBudgetScenarios(
        activeProformaWithProject?.subprojectId?.toString() || '',
        {
            enabled: enabled && !!activeProformaWithProject?.subprojectId,
            staleTime: Infinity,
            gcTime: 1000 * 60 * 60,
            refetchOnWindowFocus: false,
        },
    );

    let scenarioId: number | null = null;
    if (activeProformaWithProject && budgetScenariosData?.isEarlyPlanning && budgetScenariosData?.budgetScenarios?.length) {
        const { subprojectId } = budgetScenariosData;
        if (subprojectId) {
            try {
                const effectiveKey = getEffectiveKey(GlobalKeys.SelectedScenarioId, true, user?.id);
                const cookieValue = getCookie(effectiveKey);
                if (cookieValue) {
                    const parsedCookie = JSON.parse(cookieValue);
                    if (parsedCookie && parsedCookie[subprojectId]) {
                        const cookieScenarioId = Number(parsedCookie[subprojectId]);
                        const scenarioExists = budgetScenariosData.budgetScenarios.find(
                            scenario => scenario.id === cookieScenarioId,
                        );
                        if (scenarioExists) {
                            scenarioId = cookieScenarioId;
                        }
                    }
                }
            } catch (error) {
                console.error('Error parsing scenario cookie:', error);
            }
        }

        if (!scenarioId) {
            const storage = getLocalStorage<IProFormaPageLocalStorage>(PageSpecificKeys.ProFormaPage);
            const storedId = storage?.[activeProformaWithProject.id]?.[SELECTED_SCENARIO_KEY];
            if (storedId && !Number.isNaN(Number(storedId))) {
                const scenarioExists = budgetScenariosData.budgetScenarios.find(
                    scenario => scenario.id === Number(storedId),
                );
                if (scenarioExists) {
                    scenarioId = Number(storedId);
                }
            }
        }

        if (!scenarioId) {
            scenarioId = budgetScenariosData.budgetScenarios[0]?.id;
        }
    }

    const proFormaRoomQuery = useGetProFormaRoom(
        dealId,
        activeProformaWithProject?.id || 0,
        selectedOrganizationId!,
        true, // shouldGetActuals = true
        'Total Anticipated Cost', // forecastMetric
        scenarioId,
        {
            enabled: enabled && !!activeProformaWithProject && !isLoadingBudgetScenarios,
            staleTime: Infinity,
            gcTime: 1000 * 60 * 60,
            refetchOnWindowFocus: false,
        },
    );

    if (!activeProformaWithProject) {
        return {
            data: null,
            isLoading: false,
            error: null,
            refetch: () => {},
        };
    }

    return proFormaRoomQuery;
};
